import { signal } from '@preact/signals';
import { Shower, ShowerNow, activeShowers, apexDir, hourlyRate, sporadicRate } from '../astro/meteors';
import { DEG, Vec3, clamp, cross, norm } from '../lib/math';
import type { FrameContext, PickHit, SkyProvider } from '../render/engine';
import type { LabelCandidate } from '../render/overlay';
import { Selection, selection, settings } from '../state';

export const meteorPreview = signal(false);

interface RadiantView {
  now: ShowerNow;
  enu: Vec3;
  alt: number;
  rate: number;
  x: number;
  y: number;
  onScreen: boolean;
}

interface Streak {
  a: Vec3; // start (ENU)
  axis: Vec3; // rotation axis for the great circle of motion
  len: number; // radians travelled
  born: number;
  dur: number;
  bright: number;
  hue: [number, number, number];
}

export class MeteorService implements SkyProvider {
  showers: Shower[] = [];
  active: ShowerNow[] = [];
  views: RadiantView[] = [];
  sporadic = 0;
  private lastCalc = -1e12;
  private streaks: Streak[] = [];
  private lastSpawn = 0;

  /** Settles once the shower list has loaded, or failed to. */
  ready: Promise<void> = Promise.resolve();

  load() {
    this.ready = (async () => {
      try {
        const r = await fetch(`${import.meta.env.BASE_URL}data/showers.json`);
        this.showers = await r.json();
        this.lastCalc = -1e12;
      } catch {
        /* no showers: the rest of the sky still works */
      }
    })();
    return this.ready;
  }

  update(ctx: FrameContext) {
    this.views.length = 0;
    if (!this.showers.length) return;
    const t = ctx.date.getTime();
    if (Math.abs(t - this.lastCalc) > 60e3) {
      this.active = activeShowers(this.showers, ctx.date, 0.8);
      this.lastCalc = t;
    }
    const cam = ctx.camera;
    const tmp = { x: 0, y: 0 };
    const nelm = ctx.nelm;
    for (const a of this.active) {
      const enu = ctx.frame.toEnu(a.dir);
      const alt = Math.asin(enu[2]) / DEG;
      const rate = hourlyRate(a.zhr, alt, a.shower.r, nelm);
      const p = cam.project(enu, tmp);
      this.views.push({ now: a, enu, alt, rate, x: p?.x ?? -1e4, y: p?.y ?? -1e4, onScreen: !!p && cam.onScreen(p, 10) });
    }
    const apex = ctx.frame.toEnu(apexDir(ctx.date));
    this.sporadic = ctx.darkness > 0.5 ? sporadicRate(Math.asin(apex[2]) / DEG, nelm) : 0;
    if (meteorPreview.peek()) this.spawn(ctx);
    else this.streaks.length = 0;
  }

  /** Preview mode: meteors at 40× the expected rate, flying away from their radiants. */
  private spawn(ctx: FrameContext) {
    const now = ctx.time;
    const dt = Math.min(0.1, now - this.lastSpawn);
    this.lastSpawn = now;
    const SPEED = 40 / 3600; // per second of real time
    const sources: { enu: Vec3 | null; rate: number; v: number }[] = this.views
      .filter((v) => v.alt > 0)
      .map((v) => ({ enu: v.enu, rate: Math.max(v.rate, 1), v: v.now.shower.v }));
    sources.push({ enu: null, rate: Math.max(this.sporadic, 2), v: 40 });
    for (const s of sources) {
      if (Math.random() > s.rate * SPEED * dt) continue;
      // Random start 15–70° from the radiant (or anywhere for sporadics), above 12°.
      for (let tries = 0; tries < 12; tries++) {
        const alt = (12 + Math.random() * 70) * DEG, az = Math.random() * 2 * Math.PI;
        const a: Vec3 = [Math.cos(alt) * Math.sin(az), Math.cos(alt) * Math.cos(az), Math.sin(alt)];
        let axis: Vec3;
        let len: number;
        if (s.enu) {
          const d = Math.acos(clamp(a[0] * s.enu[0] + a[1] * s.enu[1] + a[2] * s.enu[2], -1, 1));
          if (d < 12 * DEG || d > 75 * DEG) continue;
          axis = norm(cross(s.enu, a));
          len = (6 + 22 * Math.sin(d)) * DEG * (0.6 + Math.random() * 0.6);
        } else {
          const r = norm([Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5] as Vec3);
          axis = norm(cross(a, r));
          len = (8 + Math.random() * 18) * DEG;
        }
        const fast = clamp((s.v - 15) / 55, 0, 1);
        this.streaks.push({
          a, axis, len, born: now, dur: 0.9 - 0.55 * fast + Math.random() * 0.3,
          bright: 0.5 + Math.random() * 0.8, hue: fast > 0.6 ? [0.75, 0.95, 1.0] : [1.0, 0.92, 0.78],
        });
        break;
      }
    }
    this.streaks = this.streaks.filter((m) => now - m.born < m.dur + 0.6);
  }

  overlay(g: CanvasRenderingContext2D, ctx: FrameContext) {
    const cam = ctx.camera;
    const sel = selection.peek();
    // Radiants (only in the dark, unless selected), with the showers layer on; in Naked eye, only those above the
    // horizon, where meteors can come from. Practice meteors, below, show either way.
    for (const v of settings.peek().meteors ? this.views : []) {
      if (!v.onScreen) continue;
      const isSel = sel?.kind === 'shower' && sel.code === v.now.shower.code;
      if (!isSel && (ctx.darkness < 0.3 || (ctx.nakedEye && v.alt <= 0))) continue;
      const strong = clamp(v.now.zhr / 20, 0.35, 1);
      const alpha = (v.alt > 0 ? 0.85 : 0.4) * (isSel ? 1 : 0.55 + 0.45 * strong);
      g.save();
      g.translate(v.x, v.y);
      g.strokeStyle = `rgba(206,176,255,${alpha})`;
      g.lineWidth = isSel ? 1.8 : 1.3;
      if (v.alt <= 0) g.setLineDash([2, 3]);
      g.beginPath();
      g.arc(0, 0, 5.5, 0, Math.PI * 2);
      g.stroke();
      for (let k = 0; k < 8; k++) {
        const ang = (k * Math.PI) / 4 + 0.2;
        const r0 = 8.5, r1 = 8.5 + (k % 2 ? 5 : 9) * (0.6 + 0.4 * strong);
        g.beginPath();
        g.moveTo(Math.cos(ang) * r0, Math.sin(ang) * r0);
        g.lineTo(Math.cos(ang) * r1, Math.sin(ang) * r1);
        g.stroke();
      }
      g.restore();
    }
    // Preview streaks.
    const now = ctx.time;
    const tmp = { x: 0, y: 0 };
    for (const m of this.streaks) {
      const age = now - m.born;
      const head = clamp(age / m.dur, 0, 1);
      const fade = age > m.dur ? 1 - (age - m.dur) / 0.6 : 1;
      if (fade <= 0) continue;
      const pts: { x: number; y: number }[] = [];
      const tail = Math.max(0, head - 0.45);
      for (let k = 0; k <= 8; k++) {
        const f = tail + ((head - tail) * k) / 8;
        const ang = m.len * f;
        const c = Math.cos(ang), s = Math.sin(ang);
        const [ax, ay, az] = m.axis, [px, py, pz] = m.a;
        const d = ax * px + ay * py + az * pz;
        const cr: Vec3 = [ay * pz - az * py, az * px - ax * pz, ax * py - ay * px];
        const v: Vec3 = [px * c + cr[0] * s + ax * d * (1 - c), py * c + cr[1] * s + ay * d * (1 - c), pz * c + cr[2] * s + az * d * (1 - c)];
        const p = cam.project(v, tmp);
        if (!p) break;
        pts.push({ x: p.x, y: p.y });
      }
      if (pts.length < 2) continue;
      const a = pts[0], b = pts[pts.length - 1];
      const grad = g.createLinearGradient(a.x, a.y, b.x, b.y);
      const [r, gg, bb] = m.hue.map((x) => Math.round(x * 255));
      grad.addColorStop(0, `rgba(${r},${gg},${bb},0)`);
      grad.addColorStop(1, `rgba(${r},${gg},${bb},${0.9 * m.bright * fade})`);
      g.strokeStyle = grad;
      g.lineWidth = 1.6 * m.bright;
      g.lineCap = 'round';
      g.beginPath();
      g.moveTo(a.x, a.y);
      for (const p of pts) g.lineTo(p.x, p.y);
      g.stroke();
      g.fillStyle = `rgba(255,255,255,${0.85 * fade * m.bright})`;
      g.beginPath();
      g.arc(b.x, b.y, 1.4 * m.bright, 0, Math.PI * 2);
      g.fill();
    }
  }

  labels(ctx: FrameContext, out: LabelCandidate[]) {
    if (!settings.peek().meteors) return;
    const sel = selection.peek();
    for (const v of this.views) {
      if (!v.onScreen) continue;
      if ((ctx.darkness < 0.3 || (ctx.nakedEye && v.alt <= 0)) && !(sel?.kind === 'shower' && sel.code === v.now.shower.code)) continue;
      const sub = v.alt > 0 ? (v.rate >= 1 ? `about ${Math.round(v.rate)} an hour now` : 'active, low rate') : 'radiant below horizon';
      out.push({ x: v.x, y: v.y, r: 16, text: v.now.shower.name, sub, style: 'radiant', priority: 1800 + v.now.zhr, sel: { kind: 'shower', code: v.now.shower.code } });
    }
  }

  pick(ctx: FrameContext, x: number, y: number): PickHit | null {
    if (!settings.peek().meteors) return null;
    let best: PickHit | null = null;
    for (const v of this.views) {
      if (!v.onScreen || (ctx.nakedEye && v.alt <= 0)) continue;
      const d = Math.hypot(v.x - x, v.y - y);
      if (d < 26 && (!best || d < best.dist)) best = { sel: { kind: 'shower', code: v.now.shower.code }, dist: d };
    }
    return best;
  }

  locate(sel: Selection): { enu: Vec3; label: string } | null {
    if (sel.kind !== 'shower') return null;
    const v = this.views.find((x) => x.now.shower.code === sel.code);
    return v ? { enu: v.enu, label: v.now.shower.name } : null;
  }
}
